// Canonical FAQ data. One source of truth used by:
//   • /pricing  (full set, between comparison table and final CTA)
//   • /         (FAQ_TRUST only, before the FinalCTA, so last-minute
//                objections get answered before the user is asked to
//                click Start free again)
//   • /help     (FAQ_TRUST only, under the feature walkthroughs, because
//                /help previously only answered "how do I use X" and not
//                "is this legal / safe / private")
//
// Two arrays exist because the questions split cleanly:
//   FAQ_TRUST   — existential objections. "Is this cheating?",
//                 "Does Maren use official MCAT content?", "Do you train
//                 on me?" These block the purchase decision before
//                 pricing even enters the conversation, and they're the
//                 ones a paranoid pre-med will google before clicking.
//                 Per the landing-page liability discipline, no explicit
//                 competitor or exam-maker names appear in the answers.
//   FAQ_PLANS   — buy-button friction. Cancel, refund, switch,
//                 student discount, export. Belongs near pricing.
//
// Voice: terse, founder-direct, no hedging, no marketing fluff. Every
// answer reads as something Isaiah would say in a one-line DM, not
// something a chatbot would say in an OnboardingEmail.

export interface FaqItem {
  q: string;
  a: string;
}

// Existential / trust objections. Order is intentional:
//   1. "Is this cheating" — the single biggest blocker for a high-anxiety
//      pre-med audience. Answering it first releases the rest of the
//      conversation.
//   2. Official content / IP — pre-meds have seen classmates lose their
//      seat over official-content IP violations. Answers this without
//      naming the exam-maker, per liability discipline.
//   3. Training data — Reddit r/MCAT regularly asks this about every
//      AI tool. Pre-empt it.
//   4. Data deletion — close the privacy loop. People want to know they
//      can leave clean.
//   5. General-chat comparison — the most common positioning question.
//      Answer it before the visitor asks themselves "why not just use
//      the free general chat tool I already pay for."
//   6. Wrong answers — accountability.
//
// Marketing-surface discipline: no explicit competitor or third-party
// brand names (no exam-maker name, no AI-chat-product name, no AI-
// provider name, no question-bank brand). Use category language so the
// site avoids comparative-advertising and trademark exposure.
export const FAQ_TRUST: FaqItem[] = [
  {
    q: "Is using Maren during MCAT prep considered cheating?",
    a: "No. Maren is a study tool. It coaches you on practice questions you have already missed; it does not take your exam for you. The MCAT itself is closed-book, in-person, and proctored, so there is no way Maren could be used during the test. Outside the test room, you are allowed to study with any resource you want, including AI.",
  },
  {
    q: "Does Maren use official MCAT content?",
    a: "No. We never license, scrape, or store questions from official practice products or third-party question banks. You paste your own missed questions in your own words; Maren reads them and coaches you through the underlying concepts. We treat exam-maker intellectual property the way every premed should: hands-off.",
  },
  {
    q: "Do you train AI models on my data?",
    a: "No. Your logged questions, study sessions, chats, and notes are never used to train models. Our AI provider does not train on API inputs by default, and we do not opt in. Your data stays your data.",
  },
  {
    q: "What happens to my data if I cancel?",
    a: "Your account stays active on the Free tier. Everything you logged stays yours: view, edit, delete, and export from Settings any time. Nothing is deleted when paid access ends; new analysis simply pauses until you upgrade again. (Accounts created before August 3, 2026 also keep their free weak-area dashboard, analytics, and planner; accounts created before August 19, 2026 keep free Anki access.) If you fully delete your account, your data is removed from our database. The full retention schedule is in the Privacy Policy.",
  },
  {
    q: "How is Maren different from a general AI chat tool?",
    a: "A general chat tool starts every conversation from zero. Maren reads your test date, your full question history, your error tags, your weak topics, and your study calendar before answering. Every response is personalized to what you have already missed and what is coming up on your plan.",
  },
  {
    q: "What if Maren gives me wrong information?",
    a: "Email support@joinmaren.com with the question and Maren's answer. We review every report, and the correction lands on your record so Maren learns it the next time you log a similar question. Sanity-check high-stakes answers against official sources. Maren is a coach, not a guarantee.",
  },
  {
    q: "Can AI really replace a $200-an-hour MCAT tutor?",
    a: "For most students, the question is not replace, it is reach. A real tutor at $200 an hour for 8 hours a month runs $1,600. A 3-month Maren study pass costs less than one of those hours. Maren will not match a great human on judgment calls about your specific weak spots, but it can read every question you log, every miss you make, and every full-length you take, and surface patterns no tutor working 8 hours a month would catch. The smart move is Maren full-time plus a real tutor for the last few weeks if your budget allows.",
  },
];

// Plan-mechanics questions. Lead with cancellation because it's the
// single most common reason someone abandons a paid signup mid-flow.
// All copy here mirrors what's already on /pricing today, expanded
// where the previous answers were terse.
export const FAQ_PLANS: FaqItem[] = [
  {
    q: "Do study passes auto-renew?",
    a: "No. A pass is a one-time purchase: you pay once, get full access for 3 or 6 months, and it simply ends. No card on file quietly rebilling you the month after your test.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Monthly access cancels in one click from Settings, with no retention call and no dark-pattern survey, and stays active through the end of the billing period. Study passes have nothing to cancel: they are paid once and end on their own.",
  },
  {
    q: "What if I don't like it?",
    a: "Start on Free and stay as long as you want. It never expires, so you can log your mistakes, review your full history, and see exactly how Maren works before paying anything. When you are ready, a study pass unlocks the interpretation features.",
  },
  {
    q: "How does the Free tier fit in?",
    a: "Free is not a trial and never expires. It is the mistake record: unlimited manual question logging, your own reflections, your full history, full-length score logging, and export, forever. Paid Maren is the layer that interprets that record: recurring patterns, priority weaknesses, and what to review next. If you only want a structured place to log, you never need to pay.",
  },
  {
    q: "I signed up before the free tier changed. Do I lose anything?",
    a: "No. Accounts created before the change permanently keep everything they were promised, including the free weak-area dashboard, error analytics, and planner. That is not a limited-time grace period; it does not expire. New paid features like Mistake Insights are paid for everyone.",
  },
  {
    q: "Should I buy a pass or go monthly?",
    a: "The passes are the better deal: a 3-month pass works out to about $33 a month and a 6-month pass to about $25, against $39 for monthly access. Monthly exists for students who genuinely need only a few weeks. Same full feature set either way.",
  },
  {
    q: "Is there a student discount?",
    a: "Our entire user base is students, so the base price is already the student price. We do not run separate tiers. The study-pass pricing is the discount.",
  },
  {
    q: "Can I export my data?",
    a: "Yes. Settings → Profile → Export downloads a JSON file with every logged question, session, plan task, full-length score, and chat. Bring your data with you if you ever leave.",
  },
];

// Full FAQ in display order. /pricing uses this directly.
export const FAQ_ALL: FaqItem[] = [...FAQ_TRUST, ...FAQ_PLANS];
